import React, { useState, useEffect } from 'react';
import { Search, Eye, UserX, CheckCircle, XCircle, Clock, Shield, User, X } from 'lucide-react';
import apiService from '../../services/api';
import TablePagination from '../company/JobManagement/Components/TablePagination';
import ConfirmationModal from "../../components/ConfirmationModal";
interface BackgroundVerification {
  criminal_check: string | null;
  employment_check: string | null;
  education_check: string | null;
  reference_check: string | null;
  overall_status: 'none' | 'partial' | 'complete';
}

interface Verification {
  email_verified: boolean;
  email_verified_at: string | null;
  id_verified: boolean;
  id_verified_at: string | null;
  background_verification: BackgroundVerification;
}

interface JobSeeker {
  id: string;
  name: string;
  email: string;
  job_title: string;
  location: string;
  status: string;
  tier: string;
  profile_completion: number;
  resume_count: number;
  application_count: number;
  verification: Verification;
  joined_date: string;
  years_of_experience: number;
  last_active: string | null;
}

interface JobSeekersResponse {
  job_seekers: JobSeeker[];
  total_count: number;
  average_profile_completion: number;
  stats: {
    active: number;
    email_verified: number;
    id_verified: number;
    bg_verified: number;
    with_resumes: number;
    with_applications: number;
  };
}

interface JobSeekerDetails {
  id: string;
  personal_info: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    date_of_birth: string | null;
  };
  professional_info: {
    current_job_title: string;
    years_of_experience: number;
    skills: string[];
    industry: string | null;
    salary_expectation: number | null;
  };
  location: string;
  account_status: {
    is_active: boolean;
    status: string;
    created_at: string;
    last_login: string | null;
    profile_completion: number;
  };
  verification: {
    email_verified: boolean;
    email_verified_at: string | null;
    id_verified: boolean;
    background_checks: {
      criminal_check: string | null;
      employment_check: string | null;
      education_check: string | null;
      reference_check: string | null;
    };
  };
  activity: {
    resumes: Array<{
      id: string;
      filename: string;
      uploaded_at: string;
      file_size: number;
      status: string;
    }>;
    resume_count: number;
    application_count: number;
    public_profile_enabled: boolean;
    allow_recruiter_contact: boolean;
  };
}

const JobSeekers: React.FC = () => {
  const [jobSeekers, setJobSeekers] = useState<JobSeeker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('');
  const [selectedJobSeeker, setSelectedJobSeeker] = useState<JobSeekerDetails | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    search_total: 0,
    email_verified: 0,
    id_verified: 0,
    bg_verified: 0,
    with_resumes: 0,
    with_applications: 0,
    average_completion: 0
  });
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const [pendingAction, setPendingAction] = useState<{
    id: string;
    status: string;
    reason: string;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const totalCount = stats.search_total;

  const handleSearch = () => {
    setCurrentPage(1);
    fetchJobSeekers(
      rowsPerPage,
      0,
      searchTerm,
      statusFilter,
      verificationFilter
    );
  };

  const fetchJobSeekers = async (
    limit = rowsPerPage,
    offset = (currentPage - 1) * rowsPerPage,
    search = searchTerm,
    status = statusFilter,
    verification = verificationFilter
  ) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();

      if (search) params.set("search", search);
      if (status) params.set("is_active", String(status === "active"));
      if (verification) params.set("verified", verification);
      params.set("limit", String(limit));
      params.set("offset", String(offset));

      const response = await apiService.get(
        `/super-admin/job-seekers?${params.toString()}`
      );
      const data = response.data;
      console.log('Job seekers data:', data);

      setJobSeekers(data.job_seekers || []);
      setStats({
        total: data.total_count || 0,
        active: data.stats?.active || 0,
        search_total: data.search_total || 0,
        email_verified: data.stats?.email_verified || 0,
        id_verified: data.stats?.id_verified || 0,
        bg_verified: data.stats?.bg_verified || 0,
        with_resumes: data.stats?.with_resumes || 0,
        with_applications: data.stats?.with_applications || 0,
        average_completion: data.average_profile_completion || 0
      });
    } catch (err) {
      console.error('Error fetching job seekers:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch job seekers');
      setJobSeekers([]);
      setStats({
        total: 0,
        active: 0,
        search_total: 0,
        email_verified: 0,
        id_verified: 0,
        bg_verified: 0,
        with_resumes: 0,
        with_applications: 0,
        average_completion: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const openStatusConfirmation = (
    id: string,
    status: string,
    reason: string
  ) => {
    setPendingAction({ id, status, reason });
    setConfirmationOpen(true);
  };
  const confirmStatusUpdate = async () => {
    if (!pendingAction) return;

    await updateJobSeekerStatus(
      pendingAction.id,
      pendingAction.status,
      pendingAction.reason
    );

    setConfirmationOpen(false);
    setPendingAction(null);
  };

  const fetchJobSeekerDetails = async (jobSeekerId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No access token found');
      }

      const response = await apiService.get(`/super-admin/job-seekers/${jobSeekerId}`);

      const data: JobSeekerDetails = await response.data;
      setSelectedJobSeeker(data);
      setShowDetails(true);
    } catch (err) {
      console.error('Error fetching job seeker details:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch job seeker details');
    }
  };

  const updateJobSeekerStatus = async (jobSeekerId: string, newStatus: string, reason: string = '') => {
    try {
      setActionLoading(jobSeekerId);

      const response = await apiService.put(`/super-admin/job-seekers/${jobSeekerId}/status`, {
        status: newStatus,
        reason: reason
      });

      // Refresh the job seekers list
      await fetchJobSeekers();
    } catch (err) {
      console.error('Error updating job seeker status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update job seeker status');
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    fetchJobSeekers(
      rowsPerPage,
      (currentPage - 1) * rowsPerPage,
      searchTerm,
      statusFilter,
      verificationFilter
    );
  }, [currentPage, rowsPerPage, statusFilter, verificationFilter]);

  const filteredJobSeekers = jobSeekers;

  const getStatusBadge = (status: string) => {
    const isActive = status === 'Active';
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive
        ? 'bg-green-100 text-green-800'
        : 'bg-red-100 text-red-800'
        }`}>
        {isActive ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
        {status}
      </span>
    );
  };

  const getTierBadge = (tier: string) => {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        {tier}
      </span>
    );
  };

  const getVerificationBadges = (verification: Verification) => {
    const emailBadge = (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${verification.email_verified
        ? 'bg-green-100 text-green-800'
        : 'bg-red-100 text-red-800'
        }`}>
        {verification.email_verified ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
        Email
      </span>
    );

    const idBadge = (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${verification.id_verified
        ? 'bg-green-100 text-green-800'
        : 'bg-gray-100 text-gray-800'
        }`}>
        {verification.id_verified ? <CheckCircle className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}
        ID
      </span>
    );

    const bgStatus = verification.background_verification.overall_status;
    const bgBadge = (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${bgStatus === 'complete' ? 'bg-green-100 text-green-800' :
        bgStatus === 'partial' ? 'bg-yellow-100 text-yellow-800' :
          'bg-gray-100 text-gray-800'
        }`}>
        {bgStatus === 'complete' ? <CheckCircle className="w-3 h-3 mr-1" /> :
          bgStatus === 'partial' ? <Clock className="w-3 h-3 mr-1" /> :
            <Shield className="w-3 h-3 mr-1" />}
        BG
      </span>
    );

    return (
      <div className="flex flex-col space-y-1">
        <div className="flex space-x-1">
          {emailBadge}
          {idBadge}
        </div>
        <div className="flex space-x-1">
          {bgBadge}
        </div>
      </div>
    );
  };

  const ActionButton: React.FC<{
    onClick: () => void;
    icon: React.ReactNode;
    tooltip: string;
    color: 'blue' | 'red';
    disabled?: boolean;
  }> = ({ onClick, icon, tooltip, color, disabled = false }) => {
    return (
      <div className="relative group">
        <button
          onClick={onClick}
          disabled={disabled}
          className={`p-1 rounded hover:bg-gray-100 transition-colors ${color === 'blue' ? 'text-blue-600 hover:text-blue-900' : 'text-red-600 hover:text-red-900'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {icon}
        </button>
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
          {tooltip}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading job seekers...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Job Seekers ({stats.total})</h1>
        <p className="text-gray-600">Average profile completion: {stats.average_completion}%</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center">
            <div>
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center">
            <div>
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center">
            <div>
              <p className="text-sm font-medium text-gray-600">Email ✓</p>
              <p className="text-2xl font-bold text-gray-900">{stats.email_verified}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
          <div className="flex items-center">
            <div>
              <p className="text-sm font-medium text-gray-600">ID ✓</p>
              <p className="text-2xl font-bold text-gray-900">{stats.id_verified}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center">
            <div>
              <p className="text-sm font-medium text-gray-600">BG ✓</p>
              <p className="text-2xl font-bold text-gray-900">{stats.bg_verified}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-indigo-500">
          <div className="flex items-center">
            <div>
              <p className="text-sm font-medium text-gray-600">Resumes</p>
              <p className="text-2xl font-bold text-gray-900">{stats.with_resumes}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search job seekers..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Search
          </button>
          <div className="flex gap-2">
            <select
              className="px-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex gap-2">
            <select
              className="px-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={verificationFilter}
              onChange={(e) => { setVerificationFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="">Verification</option>
              <option value="email_verified">Email Verified</option>
              <option value="identity_verified">ID Verified</option>
              <option value="bg_verified">BG Verified</option>
            </select>
          </div>
          {(searchTerm || statusFilter || verificationFilter) && (
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("");
                  setVerificationFilter("");
                  setCurrentPage(1);
                }}
                className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 hover:text-red-600"
              >
                ✕
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <XCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading job seekers</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={() => fetchJobSeekers()}
                className="mt-2 text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Seekers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Job Seeker
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Verification
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredJobSeekers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    {loading ? 'Loading...' : 'No job seekers found'}
                  </td>
                </tr>
              ) : (
                filteredJobSeekers.map((seeker) => (
                  <tr key={seeker.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {seeker.name.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{seeker.name}</div>
                          <div className="text-sm text-gray-500">{seeker.email}</div>
                          <div className="text-sm text-blue-600">{seeker.job_title}</div>
                          <div className="text-sm text-gray-400 flex items-center">
                            📍 {seeker.location}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-2">
                        {getStatusBadge(seeker.status)}
                        <span className="text-xs text-gray-500">
                          Joined: {seeker.joined_date}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900">
                            {seeker.profile_completion}% complete
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${seeker.profile_completion}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {seeker.resume_count} resumes • {seeker.application_count} applications
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getVerificationBadges(seeker.verification)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => fetchJobSeekerDetails(seeker.id)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800"
                        >
                          View
                        </button>

                        {seeker.status === "Active" ? (
                          <button
                            type="button"
                            onClick={() =>
                              openStatusConfirmation(
                                seeker.id,
                                "suspended",
                                "Suspended by admin"
                              )
                            }
                            className="text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                            disabled={actionLoading === seeker.id}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              openStatusConfirmation(
                                seeker.id,
                                "active",
                                "Reactivated by admin"
                              )
                            }
                            className="text-sm font-medium text-green-600 hover:text-green-800 disabled:opacity-50"
                            disabled={actionLoading === seeker.id}
                          >
                            Activate
                          </button>
                        )}
                        <ConfirmationModal
                          isOpen={confirmationOpen}
                          onClose={() => {
                            setConfirmationOpen(false);
                            setPendingAction(null);
                          }}
                          onConfirm={confirmStatusUpdate}
                          title={
                            pendingAction?.status === "active"
                              ? "Activate Job Seeker"
                              : "Deactivate Job Seeker"
                          }
                          message={
                            pendingAction?.status === "active"
                              ? "Are you sure you want to activate this job seeker?"
                              : "Are you sure you want to deactivate this job seeker?"
                          }
                          confirmText={
                            pendingAction?.status === "active"
                              ? "Activate"
                              : "Deactivate"
                          }
                          cancelText="Cancel"
                          type={pendingAction?.status === "active" ? "success" : "danger"}
                          loading={actionLoading !== null}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalCount > rowsPerPage && (
        <TablePagination
          currentPage={currentPage}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onRowsPerPageChange={(rows) => {
            setRowsPerPage(rows);
            setCurrentPage(1);
          }}
        />
      )}

      {/* Job Seeker Details Modal */}
      {showDetails && selectedJobSeeker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                Job Seeker Details: {selectedJobSeeker.personal_info.first_name} {selectedJobSeeker.personal_info.last_name}
              </h2>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Personal Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-sm text-gray-900">{selectedJobSeeker.personal_info.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone</label>
                    <p className="text-sm text-gray-900">{selectedJobSeeker.personal_info.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Location</label>
                    <p className="text-sm text-gray-900">{selectedJobSeeker.location}</p>
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Professional Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Current Job Title</label>
                    <p className="text-sm text-gray-900">{selectedJobSeeker.professional_info.current_job_title}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Years of Experience</label>
                    <p className="text-sm text-gray-900">{selectedJobSeeker.professional_info.years_of_experience}</p>
                  </div>
                </div>
              </div>

              {/* Verification Status */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Verification Status</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2">
                      {selectedJobSeeker.verification.email_verified ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className="font-medium">Email Verification</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedJobSeeker.verification.email_verified ? 'Verified' : 'Not verified'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2">
                      {selectedJobSeeker.verification.id_verified ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <User className="w-5 h-5 text-gray-400" />
                      )}
                      <span className="font-medium">ID Verification</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedJobSeeker.verification.id_verified ? 'Verified' : 'Not verified'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">Background Checks</span>
                    </div>
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span>Criminal:</span>
                        <span className={`px-2 py-1 rounded ${selectedJobSeeker.verification.background_checks.criminal_check === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}>
                          {selectedJobSeeker.verification.background_checks.criminal_check || 'Not done'}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>Employment:</span>
                        <span className={`px-2 py-1 rounded ${selectedJobSeeker.verification.background_checks.employment_check === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}>
                          {selectedJobSeeker.verification.background_checks.employment_check || 'Not done'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Summary */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Activity Summary</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{selectedJobSeeker.activity.resume_count}</div>
                    <div className="text-sm text-gray-600">Resumes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{selectedJobSeeker.activity.application_count}</div>
                    <div className="text-sm text-gray-600">Applications</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{selectedJobSeeker.account_status.profile_completion}%</div>
                    <div className="text-sm text-gray-600">Profile Complete</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${selectedJobSeeker.account_status.is_active ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedJobSeeker.account_status.is_active ? 'Active' : 'Inactive'}
                    </div>
                    <div className="text-sm text-gray-600">Status</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobSeekers;
